// src/hooks/useHaptics.ts

import { useState, useEffect, useCallback } from "react";
import { ImpactStyle, NotificationType } from "@capacitor/haptics";
import { Preferences } from "@capacitor/preferences";
import { hapticService } from "../services/haptics";

/**
 * Custom hook for using haptic feedback in React components
 *
 * Provides haptic feedback functions tailored for the ARC application
 */
export function useHaptics() {
  const [hapticsEnabled, setHapticsEnabled] = useState(false);

  useEffect(() => {
    // Initialize haptics state
    const init = async () => {
      // Check if the preference exists, otherwise create it
      const { value } = await Preferences.get({ key: "haptics_enabled" });
      if (value === null) {
        await Preferences.set({ key: "haptics_enabled", value: "true" });
      }
      setHapticsEnabled(hapticService.isHapticsEnabled());
    };

    init();
  }, []);

  /**
   * Toggle haptics enabled state
   */
  const toggleHaptics = useCallback(
    async (enabled?: boolean) => {
      const newState = enabled !== undefined ? enabled : !hapticsEnabled;
      await hapticService.setHapticsEnabled(newState);
      setHapticsEnabled(newState);
    },
    [hapticsEnabled]
  );

  /**
   * Trigger impact haptic feedback
   */
  const impact = useCallback((style: ImpactStyle = ImpactStyle.Medium) => {
    return hapticService.impact(style);
  }, []);

  /**
   * Trigger notification haptic feedback
   */
  const notification = useCallback(
    (type: NotificationType = NotificationType.Success) => {
      return hapticService.notification(type);
    },
    []
  );

  /**
   * Trigger vibration
   */
  const vibrate = useCallback((duration = 300) => {
    return hapticService.vibrate(duration);
  }, []);

  // Convenience methods for application-specific haptic patterns

  /**
   * Success feedback for completed activities
   */
  const successActivity = useCallback(() => {
    return hapticService.notification(NotificationType.Success);
  }, []);

  /**
   * Error feedback
   */
  const errorFeedback = useCallback(() => {
    return hapticService.notification(NotificationType.Error);
  }, []);

  /**
   * Warning feedback
   */
  const warningFeedback = useCallback(() => {
    return hapticService.notification(NotificationType.Warning);
  }, []);

  /**
   * Button tap feedback - light impact
   */
  const buttonTap = useCallback(() => {
    return hapticService.impact(ImpactStyle.Light);
  }, []);

  /**
   * Button press-and-hold feedback - heavy impact
   */
  const buttonPress = useCallback(() => {
    return hapticService.impact(ImpactStyle.Heavy);
  }, []);

  /**
   * Refresh feedback
   */
  const refreshFeedback = useCallback(() => {
    return hapticService.impact(ImpactStyle.Medium);
  }, []);

  /**
   * Well-being selection feedback
   */
  const emotionSelect = useCallback(() => {
    return hapticService.impact(ImpactStyle.Medium);
  }, []);

  /**
   * Slider/wheel selection feedback
   */
  const selectionFeedback = useCallback(() => {
    return hapticService.selectionChanged();
  }, []);

  return {
    hapticsEnabled,
    toggleHaptics,
    impact,
    notification,
    vibrate,

    // App-specific feedback patterns
    successActivity,
    errorFeedback,
    warningFeedback,
    buttonTap,
    buttonPress,
    refreshFeedback,
    emotionSelect,
    selectionFeedback,
  };
}

export default useHaptics;
